import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton, SCInput, SCSwitch } from 'rollup-allspark';
import {
  STICKER_PRINTER_COMMAND_TYPES,
  STICKER_PRINTER_TYPES,
} from '../../../config/constants/printerProfiles';
import usePrinter from '../../../hooks/usePrinter';
import { cn, isEmpty } from '../../../utils';
import { unmapProfile } from '../../../utils/printerProfile';
import SelectionRadio from '../SelectionRadio';
import { getList, saveEntity, updateEntity } from '../actions';
import { PRINTER_PROFILE_SAVE } from '../sagaMap';
import { makeSelectProperty } from '../selector';

const General = ({
  getPrinterProfiles,
  isSaving,
  selectedBranch,
  selectedProfile,
  saveProfile,
  updateField,
}) => {
  const [profile, setProfile] = useState(selectedProfile);

  const { getProfiles } = usePrinter({
    getPrinterProfiles,
    selectedBranch,
    updateField,
  });

  useEffect(() => {
    setProfile({ ...selectedProfile });
  }, [selectedProfile]);

  const onPrinterTypeClicked = pType => {
    const childType = !isEmpty(pType.childTypes) ? pType.childTypes[0] : null;
    const mappedProfile = {
      ...profile,
      printerType: pType,
      printerChildType: childType,
    };

    setProfile(mappedProfile);
  };

  const onSaveClicked = () => {
    const unMappedProfile = unmapProfile(profile);
    const data = { ...unMappedProfile, branch_id: selectedBranch.id };

    saveProfile({
      ...PRINTER_PROFILE_SAVE,
      data,
      callbackFn: () => {
        getProfiles(selectedBranch);

        updateField({
          selectedProfile: profile,
        });
      },
    });
  };

  const printerTypeEl = (() => {
    const listEl = STICKER_PRINTER_TYPES.map(pType => {
      const childEl = (() => {
        if (isEmpty(pType.childTypes)) {
          return null;
        }

        const childPrinterList = pType.childTypes.map(cType => {
          const isSelected =
            pType.id === profile?.printerType?.id &&
            cType.id === profile?.printerChildType?.id;

          return (
            <div
              className={cn({
                'flex flex-1 gap-x-2 px-4 py-2 border rounded-lg cursor-pointer': true,
                'border-primary-light bg-primary-lighter': isSelected,
                'border-grey': !isSelected,
              })}
              role="presentation"
              onClick={() =>
                setProfile({
                  ...profile,
                  printerType: pType,
                  printerChildType: cType,
                })
              }
            >
              <SelectionRadio isSelected={isSelected} />
              {cType.title}
            </div>
          );
        });

        return <div className="flex gap-x-2">{childPrinterList}</div>;
      })();

      return (
        <div className="py-2 flex flex-col gap-y-4">
          <div
            className="flex gap-x-2 items-center cursor-pointer"
            role="presentation"
            onClick={() => onPrinterTypeClicked(pType)}
          >
            <SelectionRadio
              isSelected={pType.id === profile?.printerType?.id}
            />
            {pType.title}
          </div>
          {childEl}
        </div>
      );
    });

    return (
      <div className="flex flex-col gap-y-2">
        <div className="font-medium">Connection Type</div>
        {listEl}
      </div>
    );
  })();

  const commandTypeEl = (() => {
    const listEl = STICKER_PRINTER_COMMAND_TYPES.map(type => {
      const isSelected = type.id === profile?.sticker_printer_command_type_id;

      return (
        <div
          className={cn({
            'flex flex-1 gap-x-2 px-4 py-2 border rounded-lg cursor-pointer': true,
            'border-primary-light bg-primary-lighter': isSelected,
            'border-grey': !isSelected,
          })}
          role="presentation"
          onClick={() =>
            setProfile({ ...profile, sticker_printer_command_type_id: type.id })
          }
        >
          <SelectionRadio isSelected={isSelected} />
          {type.title}
        </div>
      );
    });

    return (
      <div className="flex flex-col gap-y-2">
        <div className="font-medium">Command Type</div>
        <div className="flex gap-x-4">{listEl}</div>
      </div>
    );
  })();

  return (
    <div className="w-full flex flex-col gap-y-4">
      <SCInput
        label={
          <div className="font-medium text-sm text-black">Profile Name</div>
        }
        value={profile?.profile_name}
        handleChange={e =>
          setProfile({ ...profile, profile_name: e.target.value })
        }
        type="text"
        name="profile_name"
        inputClass="border rounded-lg"
        hasError={isEmpty(profile?.profile_name)}
        hint={isEmpty(profile?.profile_name) ? 'Profile name is required' : ''}
      />
      <div className="flex gap-x-8">
        <div className="w-1/2 flex flex-col gap-y-4 mt-4">
          {printerTypeEl}
          {commandTypeEl}
        </div>
        <div className="w-1/2 flex flex-col gap-y-2 mt-4">
          <div className="font-medium">Preferences</div>
          <div className="bg-grey-lighter rounded-lg p-4 flex justify-between">
            <div className="flex gap-x-3">
              <i className="kt-receipt text-xl flex items-center" />
              <div className="flex flex-col gap-y-1">
                <div className="font-medium">Auto Print</div>
                <div className="text-xs text-grey-darker">
                  Auto print sticker when order is accepted
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <SCSwitch
                value={profile.sticker_printer_auto_print_enabled}
                handleChange={v =>
                  setProfile({
                    ...profile,
                    sticker_printer_auto_print_enabled: v,
                  })
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-end mt-4">
        <SCButton
          disabled={
            isSaving ||
            isEmpty(profile?.profile_name) ||
            isEmpty(profile?.printerType) ||
            isEmpty(profile?.printerChildType)
          }
          label={isSaving ? 'Saving...' : 'Save'}
          action={onSaveClicked}
        />
      </div>
    </div>
  );
};

General.propTypes = {
  getPrinterProfiles: PropTypes.func,
  isSaving: PropTypes.bool,
  selectedBranch: PropTypes.object,
  selectedProfile: PropTypes.object,
  saveProfile: PropTypes.func,
  updateField: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  isSaving: makeSelectProperty('isSaving'),
  selectedBranch: makeSelectProperty('selectedBranch'),
  selectedProfile: makeSelectProperty('selectedProfile'),
});

const mapDispatchToProps = dispatch => ({
  getPrinterProfiles: payload => dispatch(getList(payload)),
  updateField: payload => dispatch(updateEntity(payload)),
  saveProfile: payload => dispatch(saveEntity(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(General);
